import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";
import { fmtCurrency, fmtNumber } from "@/lib/format";

const axisStyle = { fontSize: 11, fill: "#525252", fontFamily: "IBM Plex Mono, monospace" };

const CurrencyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-300 px-3 py-2 text-xs shadow-none">
      <div className="font-semibold text-gray-900 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 font-mono-metric">
          <span className="w-2 h-2" style={{ background: p.color }}></span>
          <span className="text-gray-600">{p.name}:</span>
          <span className="text-gray-900 font-semibold">
            {typeof p.value === "number" && p.dataKey !== "patients" && p.dataKey !== "volume"
              ? fmtCurrency(p.value, true)
              : fmtNumber(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export function HorizontalRevenueBar({ data, dataKey = "revenue", color = "#5E3BFF", height = 360, testId }) {
  return (
    <div style={{ width: "100%", height }} data-testid={testId}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid stroke="#F3F4F6" horizontal={false} />
          <XAxis
            type="number"
            tick={axisStyle}
            tickFormatter={(v) => fmtCurrency(v, true)}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <YAxis
            dataKey="name"
            type="category"
            width={150}
            tick={{ ...axisStyle, fontFamily: "IBM Plex Sans" }}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <Tooltip content={<CurrencyTooltip />} cursor={{ fill: "#F3F4F6" }} />
          <Bar dataKey={dataKey} fill={color} radius={[0, 0, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function VerticalCostRevenueBar({ data, height = 360, testId }) {
  return (
    <div style={{ width: "100%", height }} data-testid={testId}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 60 }}>
          <CartesianGrid stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ ...axisStyle, fontFamily: "IBM Plex Sans" }}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
            interval={0}
            angle={-20}
            textAnchor="end"
          />
          <YAxis
            tick={axisStyle}
            tickFormatter={(v) => fmtCurrency(v, true)}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <Tooltip content={<CurrencyTooltip />} cursor={{ fill: "#F3F4F6" }} />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} />
          <Bar dataKey="revenue" name="Revenue" fill="#5E3BFF" />
          <Bar dataKey="cost" name="Cost" fill="#DC2626" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MarginBar({ data, height = 320, testId }) {
  return (
    <div style={{ width: "100%", height }} data-testid={testId}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid stroke="#F3F4F6" horizontal={false} />
          <XAxis
            type="number"
            tick={axisStyle}
            tickFormatter={(v) => fmtCurrency(v, true)}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <YAxis
            dataKey="name"
            type="category"
            width={150}
            tick={{ ...axisStyle, fontFamily: "IBM Plex Sans" }}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <Tooltip content={<CurrencyTooltip />} cursor={{ fill: "#F3F4F6" }} />
          <ReferenceLine x={0} stroke="#0A0A0A" />
          <Bar dataKey="margin" name="Margin">
            {data.map((d, i) => (
              <Cell key={i} fill={d.margin >= 0 ? "#16A34A" : "#DC2626"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function TrendLineChart({ history, forecast, metric = "revenue", height = 340, testId }) {
  // Combine history + forecast into unified points with separate keys
  const combined = [
    ...history.map((h) => ({ month: h.month, [metric]: h[metric], forecastKey: null })),
    ...forecast.map((f) => ({ month: f.month, [metric]: null, forecastKey: f[metric] })),
  ];

  // Bridge point: last history point duplicated in forecast line for continuity
  if (history.length && forecast.length) {
    const last = history[history.length - 1];
    combined.splice(history.length, 0, {
      month: last.month,
      [metric]: null,
      forecastKey: last[metric],
      _bridge: true,
    });
  }

  return (
    <div style={{ width: "100%", height }} data-testid={testId}>
      <ResponsiveContainer>
        <LineChart data={combined} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid stroke="#F3F4F6" vertical={false} />
          <XAxis
            dataKey="month"
            tick={axisStyle}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <YAxis
            tick={axisStyle}
            tickFormatter={(v) => (metric === "revenue" ? fmtCurrency(v, true) : fmtNumber(v))}
            axisLine={{ stroke: "#E5E7EB" }}
            tickLine={false}
          />
          <Tooltip content={<CurrencyTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Sans" }} />
          <Line
            type="monotone"
            dataKey={metric}
            name={metric === "revenue" ? "Revenue" : "Patients"}
            stroke="#5E3BFF"
            strokeWidth={2.5}
            dot={{ r: 3, fill: "#5E3BFF" }}
            activeDot={{ r: 5 }}
            connectNulls={false}
            isAnimationActive
          />
          <Line
            type="monotone"
            dataKey="forecastKey"
            name="Forecast"
            stroke="#6366F1"
            strokeWidth={2.5}
            strokeDasharray="6 4"
            dot={{ r: 3, fill: "#6366F1" }}
            connectNulls
            isAnimationActive
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
